import React, { useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TextB,
  TextItalic,
  TextUnderline,
  ListBullets,
  ListNumbers,
  Quotes,
  TextH,
  Image as ImageIcon,
  Link as LinkIcon,
  ArrowUUpLeft,
  ArrowUUpRight
} from '@phosphor-icons/react';

export const RichTextEditor = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: 'Start writing your story...' })
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose max-w-none focus:outline-none min-h-[500px] px-6 py-4',
        'data-testid': 'rich-text-editor'
      }
    }
  });

  const handleImageUpload = useCallback(async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('file', file);

      try {
        toast.info('Uploading image...');
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/upload/image`,
          formData,
          {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );

        const path = res.data.url;
        const imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;
        editor?.chain().focus().setImage({ src: imageUrl }).run();
        toast.success('Image uploaded');
      } catch (error) {
        console.error('Upload failed:', error);
        toast.error('Failed to upload image');
      }
    };
    input.click();
  }, [editor]);

  const handleLinkAdd = useCallback(() => {
    const url = window.prompt('Enter URL');
    if (url) {
      editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  if (!editor) return null;

  const buttons = [
    { icon: <TextB size={18} weight="bold" />, action: () => editor.chain().focus().toggleBold().run(), active: editor.isActive('bold'), test: 'bold-button' },
    { icon: <TextItalic size={18} weight="bold" />, action: () => editor.chain().focus().toggleItalic().run(), active: editor.isActive('italic'), test: 'italic-button' },
    { icon: <TextH size={18} weight="bold" />, action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(), active: editor.isActive('heading', { level: 2 }), test: 'heading-button' },
    { icon: <ListBullets size={18} weight="bold" />, action: () => editor.chain().focus().toggleBulletList().run(), active: editor.isActive('bulletList'), test: 'bullet-list-button' },
    { icon: <ListNumbers size={18} weight="bold" />, action: () => editor.chain().focus().toggleOrderedList().run(), active: editor.isActive('orderedList'), test: 'ordered-list-button' },
    { icon: <Quotes size={18} weight="bold" />, action: () => editor.chain().focus().toggleBlockquote().run(), active: editor.isActive('blockquote'), test: 'blockquote-button' },
    { icon: <LinkIcon size={18} weight="bold" />, action: handleLinkAdd, active: editor.isActive('link'), test: 'link-button' },
    { icon: <ImageIcon size={18} weight="bold" />, action: handleImageUpload, active: false, test: 'image-upload-button' }
  ];

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b border-border bg-background sticky top-16 z-30">
        {buttons.map((btn, idx) => (
          <button
            key={idx}
            onClick={btn.action}
            type="button"
            data-testid={btn.test}
            className={`p-2 rounded transition-colors ${btn.active ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
          >
            {btn.icon}
          </button>
        ))}
        <div className="border-l border-border h-6 mx-1"></div>
        <button
          onClick={() => editor.chain().focus().undo().run()}
          type="button"
          data-testid="undo-button"
          className="p-2 rounded hover:bg-muted transition-colors"
        >
          <ArrowUUpLeft size={18} weight="bold" />
        </button>
        <button
          onClick={() => editor.chain().focus().redo().run()}
          type="button"
          data-testid="redo-button"
          className="p-2 rounded hover:bg-muted transition-colors"
        >
          <ArrowUUpRight size={18} weight="bold" />
        </button>
      </div>

      {/* Editor */}
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
